import { supabase } from "./supabase";
import {
  ActionBrief,
  GenerateActionBriefInput,
  buildOpportunityFingerprint,
  buildOpportunityKey,
  COMPETITIVE_COMPLETION_MEMORY_DAYS,
  getCompetitiveSemanticIdentity,
} from "./actionBriefs";

export type GenerateActionBriefResult = {
  actionBrief: ActionBrief;
  fromCache: boolean;
  completionMemoryMatch?: boolean;
};


type ActionBriefEntityRole = {
  raw_entity_name: string | null;
  normalized_entity_name: string | null;
  entity_role: string | null;
  score_treatment: string | null;
  is_active?: boolean | null;
};

type AccountBrandGuidanceRow = {
  id: string;
  updated_at: string | null;
  raw_guidance: string | null;
  brand_voice_summary: string | null;
  tone_do: unknown;
  tone_dont: unknown;
  preferred_terms: unknown;
  banned_terms: unknown;
  approved_claims: unknown;
  claims_to_avoid: unknown;
  audience_notes: string | null;
  positioning_notes: string | null;
  differentiators: unknown;
  cta_preferences: string | null;
  compliance_notes: string | null;
  oe_guardrails: string | null;
};

function normalizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

async function applyAccountBrandGuidance(
  input: GenerateActionBriefInput
): Promise<GenerateActionBriefInput> {
  const { data, error } = await supabase
    .from("account_brand_guidance")
    .select(
      [
        "id",
        "updated_at",
        "raw_guidance",
        "brand_voice_summary",
        "tone_do",
        "tone_dont",
        "preferred_terms",
        "banned_terms",
        "approved_claims",
        "claims_to_avoid",
        "audience_notes",
        "positioning_notes",
        "differentiators",
        "cta_preferences",
        "compliance_notes",
        "oe_guardrails",
      ].join(",")
    )
    .eq("account_id", input.accountId)
    .eq("apply_to_opportunity_engine", true)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn("[ActionBrief] Brand guidance lookup failed", error);
    return input;
  }

  if (!data) return input;

  const row = data as AccountBrandGuidanceRow;
  const guidanceDifferentiators = normalizeStringList(row.differentiators);
  const existingDifferentiators = input.accountContext?.differentiators || [];

  return {
    ...input,
    accountContext: {
      ...(input.accountContext || {}),
      differentiators: Array.from(
        new Set([...existingDifferentiators, ...guidanceDifferentiators])
      ),
      brandGuidance: {
        id: row.id,
        updatedAt: row.updated_at || undefined,
        rawGuidance: row.raw_guidance || undefined,
        brandVoiceSummary: row.brand_voice_summary || undefined,
        toneDo: normalizeStringList(row.tone_do),
        toneDont: normalizeStringList(row.tone_dont),
        preferredTerms: normalizeStringList(row.preferred_terms),
        bannedTerms: normalizeStringList(row.banned_terms),
        approvedClaims: normalizeStringList(row.approved_claims),
        claimsToAvoid: normalizeStringList(row.claims_to_avoid),
        audienceNotes: row.audience_notes || undefined,
        positioningNotes: row.positioning_notes || undefined,
        differentiators: guidanceDifferentiators,
        ctaPreferences: row.cta_preferences || undefined,
        complianceNotes: row.compliance_notes || undefined,
        oeGuardrails: row.oe_guardrails || undefined,
      },
    },
  };
}

function buildBrandGuidancePromptLines(
  input: GenerateActionBriefInput
): string[] {
  const guidance = input.accountContext?.brandGuidance;
  if (!guidance) return [];

  const lines = [
    "",
    "Customer-approved brand and compliance guidance:",
  ];

  if (guidance.brandVoiceSummary) {
    lines.push(`- Brand voice: ${guidance.brandVoiceSummary}`);
  }

  if (guidance.audienceNotes) {
    lines.push(`- Audience guidance: ${guidance.audienceNotes}`);
  }

  if (guidance.positioningNotes) {
    lines.push(`- Positioning guidance: ${guidance.positioningNotes}`);
  }

  if (guidance.toneDo?.length) {
    lines.push(`- Tone requirements: ${guidance.toneDo.join("; ")}`);
  }

  if (guidance.toneDont?.length) {
    lines.push(`- Tone to avoid: ${guidance.toneDont.join("; ")}`);
  }

  if (guidance.preferredTerms?.length) {
    lines.push(`- Preferred terminology: ${guidance.preferredTerms.join("; ")}`);
  }

  if (guidance.bannedTerms?.length) {
    lines.push(`- Prohibited terminology: ${guidance.bannedTerms.join("; ")}`);
  }

  if (guidance.approvedClaims?.length) {
    lines.push(`- Approved claims: ${guidance.approvedClaims.join("; ")}`);
  }

  if (guidance.claimsToAvoid?.length) {
    lines.push(`- Claims to avoid: ${guidance.claimsToAvoid.join("; ")}`);
  }

  if (guidance.ctaPreferences) {
    lines.push(`- CTA guidance: ${guidance.ctaPreferences}`);
  }

  if (guidance.complianceNotes) {
    lines.push(
      `- Mandatory compliance requirement: ${guidance.complianceNotes}`
    );
  }

  if (guidance.oeGuardrails) {
    lines.push(`- Opportunity Engine guardrails: ${guidance.oeGuardrails}`);
  }

  if (guidance.rawGuidance) {
    lines.push(`- Additional account guidance: ${guidance.rawGuidance}`);
  }

  lines.push(
    "- Treat these instructions as mandatory account constraints, not optional suggestions.",
    "- Where compliance limits a recommendation, provide a compliant alternative rather than ignoring the constraint."
  );

  return lines;
}

function buildBrandGuidanceGuardrails(
  input: GenerateActionBriefInput
): string[] {
  const guidance = input.accountContext?.brandGuidance;
  if (!guidance) return [];

  return [
    guidance.complianceNotes
      ? `Mandatory compliance: ${guidance.complianceNotes}`
      : null,
    guidance.oeGuardrails
      ? `Opportunity Engine guardrail: ${guidance.oeGuardrails}`
      : null,
    ...(guidance.claimsToAvoid || []).map(
      (claim) => `Do not make or imply this claim: ${claim}`
    ),
    ...(guidance.bannedTerms || []).map(
      (term) => `Do not use prohibited terminology: ${term}`
    ),
    guidance.brandVoiceSummary
      ? `Follow the approved brand voice: ${guidance.brandVoiceSummary}`
      : null,
  ].filter((item): item is string => Boolean(item));
}

function normalizeEntityKey(value: string | null | undefined): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function replaceEntityText(value: string | undefined, rawName: string, canonicalName: string): string | undefined {
  if (!value || !rawName || !canonicalName || rawName === canonicalName) return value;

  const escaped = rawName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return value.replace(new RegExp(escaped, "gi"), canonicalName);
}

function opportunityReferencesEntity(input: GenerateActionBriefInput, entityName: string | null | undefined): boolean {
  const key = normalizeEntityKey(entityName);
  if (!key) return false;

  const haystack = [
    input.opportunity.title,
    input.opportunity.summary,
    input.opportunity.recommendation,
    input.opportunity.category,
    input.opportunity.intent,
    ...(input.opportunity.evidence || []),
    ...(input.opportunity.competitors || []),
    ...(input.accountContext?.competitors || []),
  ]
    .filter(Boolean)
    .map((value) => normalizeEntityKey(String(value)))
    .join("|");

  return haystack.includes(key);
}

type StaleActionBriefCandidate = {
  id: string | null;
  title?: string | null;
  recommended_action?: string | null;
  why_this_matters?: string | null;
  ai_writing_prompt?: string | null;
};

function valueContainsEntityKey(value: unknown, entityKey: string): boolean {
  return Boolean(entityKey) && normalizeEntityKey(String(value || "")).includes(entityKey);
}


async function deactivateStaleEntityGovernedBriefs(input: GenerateActionBriefInput): Promise<void> {
  const accountId = String(input.accountId || "").trim();
  const productArea = String(input.productArea || "").trim();
  const runId = String(input.runId || "").trim();
  const opportunityKey = String(input.opportunityKey || "").trim();
  const opportunityFingerprint = String(input.opportunityFingerprint || "").trim();

  // Cleanup is optional housekeeping. Require a stable scope before reading or
  // updating anything so it can never interfere with the generation path.
  if (!accountId || !productArea || (!runId && !opportunityKey && !opportunityFingerprint)) return;

  try {
    const { data: roleData, error: roleError } = await supabase
      .from("account_entity_roles")
      .select("raw_entity_name, normalized_entity_name, entity_role, score_treatment, is_active")
      .eq("account_id", accountId)
      .eq("is_active", true);

    // Expected cleanup misses are intentionally silent. Action Brief
    // generation must remain observable through its own errors below.
    if (roleError) return;

    const roles = ((roleData || []) as ActionBriefEntityRole[]).filter((role) => {
      const rawName = String(role.raw_entity_name || "").trim();
      const canonicalName = String(role.normalized_entity_name || "").trim();

      return (
        rawName &&
        canonicalName &&
        normalizeEntityKey(rawName) !== normalizeEntityKey(canonicalName) &&
        opportunityReferencesEntity(input, canonicalName)
      );
    });

    if (!roles.length) return;

    // Never interpolate entity names into a PostgREST filter. Names can contain
    // commas, periods, quotes, parentheses, or filter syntax characters. Read
    // the already-scoped candidates, then compare normalized keys locally.
    let candidateQuery = supabase
      .from("opportunity_action_briefs")
      .select("id, title, recommended_action, why_this_matters, ai_writing_prompt")
      .eq("account_id", accountId)
      .eq("product_area", productArea)
      .eq("is_active", true);

    if (runId) {
      candidateQuery = candidateQuery.eq("run_id", runId);
    } else if (opportunityKey) {
      candidateQuery = candidateQuery.eq("opportunity_key", opportunityKey);
    } else if (opportunityFingerprint) {
      candidateQuery = candidateQuery.eq("opportunity_fingerprint", opportunityFingerprint);
    }

    const { data: candidateData, error: candidateError } = await candidateQuery;
    if (candidateError) return;

    const candidates = (candidateData || []) as StaleActionBriefCandidate[];
    const staleReason =
      "Deactivated during Agent Smith v1.3 action brief generation because entity governance canonicalized this opportunity target.";

    for (const role of roles) {
      const rawKey = normalizeEntityKey(role.raw_entity_name);
      if (!rawKey) continue;

      const matchingCandidates = candidates.filter((candidate) =>
        [candidate.title, candidate.recommended_action, candidate.why_this_matters, candidate.ai_writing_prompt]
          .some((value) => valueContainsEntityKey(value, rawKey))
      );

      for (const candidate of matchingCandidates) {
        const candidateId = String(candidate.id || "").trim();
        if (!candidateId) continue;

        // Update by the stable row identifier after the scoped read. A failed
        // best-effort cleanup is deliberately ignored.
        await supabase
          .from("opportunity_action_briefs")
          .update({
            is_active: false,
            generation_reason: staleReason,
          })
          .eq("id", candidateId)
          .eq("account_id", accountId)
          .eq("is_active", true);
      }
    }
  } catch {
    // Cleanup must never block or add console noise to successful generation.
  }
}

async function applyActionBriefEntityGovernance(
  input: GenerateActionBriefInput
): Promise<GenerateActionBriefInput> {
  const { data, error } = await supabase
    .from("account_entity_roles")
    .select("raw_entity_name, normalized_entity_name, entity_role, score_treatment, is_active")
    .eq("account_id", input.accountId)
    .eq("is_active", true);

  if (error) {
    console.warn("[ActionBrief] Entity governance lookup failed", error);
    return input;
  }

  const roles = ((data || []) as ActionBriefEntityRole[]).filter((role) => {
    return role.raw_entity_name || role.normalized_entity_name;
  });

  const suppressingRole = roles.find((role) => {
    const treatment = String(role.score_treatment || "");
    const entityRole = String(role.entity_role || "");
    const isSuppressive =
      treatment === "suppress_everywhere" ||
      entityRole === "suppress";

    return (
      isSuppressive &&
      (
        opportunityReferencesEntity(input, role.raw_entity_name) ||
        opportunityReferencesEntity(input, role.normalized_entity_name)
      )
    );
  });

  if (suppressingRole) {
    const blockedName =
      suppressingRole.raw_entity_name ||
      suppressingRole.normalized_entity_name ||
      "this entity";

    throw new Error(
      `Action brief blocked: ${blockedName} is marked ${suppressingRole.score_treatment || suppressingRole.entity_role} for this account. Review entity governance before generating content.`
    );
  }

  let nextInput: GenerateActionBriefInput = {
    ...input,
    opportunity: {
      ...input.opportunity,
      evidence: input.opportunity.evidence ? [...input.opportunity.evidence] : undefined,
      competitors: input.opportunity.competitors ? [...input.opportunity.competitors] : undefined,
    },
    accountContext: input.accountContext
      ? {
          ...input.accountContext,
          competitors: input.accountContext.competitors ? [...input.accountContext.competitors] : undefined,
          differentiators: input.accountContext.differentiators ? [...input.accountContext.differentiators] : undefined,
        }
      : undefined,
  };

  roles.forEach((role) => {
    const rawName = String(role.raw_entity_name || "").trim();
    const canonicalName = String(role.normalized_entity_name || "").trim();

    if (!rawName || !canonicalName || normalizeEntityKey(rawName) === normalizeEntityKey(canonicalName)) {
      return;
    }

    const treatment = String(role.score_treatment || "");
    const entityRole = String(role.entity_role || "");
    const canCanonicalize =
      treatment === "include_primary" ||
      treatment === "include_secondary" ||
      treatment === "show_only" ||
      entityRole === "direct_competitor" ||
      entityRole === "adjacent_competitor";

    if (!canCanonicalize || !opportunityReferencesEntity(nextInput, rawName)) {
      return;
    }

    nextInput = {
      ...nextInput,
      opportunity: {
        ...nextInput.opportunity,
        title: replaceEntityText(nextInput.opportunity.title, rawName, canonicalName) || nextInput.opportunity.title,
        summary: replaceEntityText(nextInput.opportunity.summary, rawName, canonicalName),
        recommendation: replaceEntityText(nextInput.opportunity.recommendation, rawName, canonicalName),
        evidence: nextInput.opportunity.evidence?.map((item) => replaceEntityText(item, rawName, canonicalName) || item),
        competitors: nextInput.opportunity.competitors?.map((item) => {
          const itemKey = normalizeEntityKey(item);
          const rawKey = normalizeEntityKey(rawName);
          return itemKey === rawKey ? canonicalName : replaceEntityText(item, rawName, canonicalName) || item;
        }),
      },
      accountContext: nextInput.accountContext
        ? {
            ...nextInput.accountContext,
            competitors: nextInput.accountContext.competitors?.map((item) => {
              const itemKey = normalizeEntityKey(item);
              const rawKey = normalizeEntityKey(rawName);
              return itemKey === rawKey ? canonicalName : replaceEntityText(item, rawName, canonicalName) || item;
            }),
          }
        : nextInput.accountContext,
    };
  });

  return nextInput;
}

function humanizeLabel(value: string | null | undefined): string {
  return String(value || "visibility opportunity")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function extractComparedEntity(title: string | undefined): string | null {
  const value = String(title || "").trim();

  const patterns = [
    /vs\.?\s+(.+)$/i,
    /versus\s+(.+)$/i,
    /against\s+(.+)$/i,
    /close gap with\s+(.+)$/i,
    /close gap vs\.?\s+(.+)$/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) {
      return match[1].trim();
    }
  }

  return null;
}

function inferAssetType(input: GenerateActionBriefInput): string {
  const combined = [
    input.opportunity.title,
    input.opportunity.summary,
    input.opportunity.recommendation,
    input.opportunity.category,
    input.opportunity.intent,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (combined.includes("competitor") || combined.includes("comparison")) {
    return input.productArea === "local"
      ? "local comparison and positioning page"
      : "competitive comparison and positioning page";
  }

  if (combined.includes("service") || combined.includes("local")) {
    return "local service or service-area page";
  }

  if (combined.includes("faq") || combined.includes("question")) {
    return "FAQ-supported educational page";
  }

  if (combined.includes("entity") || combined.includes("authority")) {
    return "entity-strengthening content page";
  }

  return input.productArea === "local"
    ? "local visibility improvement page"
    : "strategic content page";
}

function buildPromptAudience(input: GenerateActionBriefInput): string {
  if (input.productArea === "local") {
    return "local customers comparing providers, services, trust signals, reviews, service-area relevance, and reasons to contact one business over another";
  }

  return "buyers comparing category options, alternatives, use cases, and reasons to choose one provider over another";
}

function buildKnownFacts({
  accountName,
  categoryLabel,
  location,
  comparedEntity,
  input,
}: {
  accountName: string;
  categoryLabel: string;
  location?: string;
  comparedEntity?: string | null;
  input: GenerateActionBriefInput;
}): string[] {
  const facts = [
    `Business/entity to write for: ${accountName}`,
    `Business/category context: ${categoryLabel}`,
  ];

  if (location) {
    facts.push(`Market/location context: ${location}`);
  }

  if (comparedEntity) {
    facts.push(`Competitor/entity creating visibility pressure: ${comparedEntity}`);
  }

  facts.push(`Opportunity: ${input.opportunity.title}`);

  if (input.opportunity.summary) {
    facts.push(`Visibility issue: ${input.opportunity.summary}`);
  }

  if (input.opportunity.recommendation) {
    facts.push(`Recommended action: ${input.opportunity.recommendation}`);
  }

  return facts;
}

function buildMockActionBriefPayload(
  input: GenerateActionBriefInput,
  opportunityKey: string,
  opportunityFingerprint: string,
  userId: string | null
): Omit<ActionBrief, "id" | "created_at" | "updated_at"> {
  const accountName = input.accountContext?.accountName || "the business";

  const category =
    input.accountContext?.category ||
    input.opportunity.category ||
    "the category";

  const categoryLabel = humanizeLabel(category);
  const location = input.accountContext?.location || input.opportunity.location;
  const assetType = inferAssetType(input);
  const audience = buildPromptAudience(input);
  const comparedEntity = extractComparedEntity(input.opportunity.title);

  const differentiators = input.accountContext?.differentiators || [];

  const knownSummary =
    input.opportunity.summary ||
    `${accountName} has an opportunity to strengthen its visibility for ${categoryLabel}.`;

  const knownRecommendation =
    input.opportunity.recommendation ||
    "Create or strengthen content that directly addresses this visibility gap.";

  const primaryGoal =
    input.productArea === "local"
      ? `Help AI systems and local buyers understand why ${accountName} should be considered for relevant ${categoryLabel} searches${location ? ` in ${location}` : ""}.`
      : `Help AI systems and buyers understand why ${accountName} should be considered, compared, and recommended in ${categoryLabel}.`;

  const recommendedAction = comparedEntity
    ? `Create or strengthen a ${assetType} that helps buyers understand why ${accountName} should be considered alongside or instead of ${comparedEntity}.`
    : `Create or strengthen a ${assetType} that helps buyers understand why ${accountName} is relevant for this opportunity.`;

  const knownFacts = buildKnownFacts({
    accountName,
    categoryLabel,
    location,
    comparedEntity,
    input,
  });

  const differentiatorLines =
    differentiators.length > 0
      ? differentiators.map((item) => `- ${item}`)
      : [
          "- Add verified proof points such as reviews, credentials, completed work examples, warranties, service process details, or local experience.",
          "- Do not invent differentiators. Use placeholders where details must be confirmed.",
        ];

  return {
    account_id: input.accountId,
    run_id: input.runId ?? null,
    product_area: input.productArea,

    opportunity_key: opportunityKey,
    opportunity_fingerprint: opportunityFingerprint,

    action_stage: 1,
    action_type: "create_foundational_asset",

    title: `Action Brief: ${input.opportunity.title}`,
    recommended_action: recommendedAction,
    why_this_matters: knownSummary,

    ai_writing_prompt: [
      `Create a draft-ready content brief for ${accountName}.`,
      "",
      "Known facts:",
      ...knownFacts.map((fact) => `- ${fact}`),
      ...buildBrandGuidancePromptLines(input),
      "",
      `Recommended content asset: ${assetType}`,
      `Primary audience: ${audience}.`,
      "",
      "Primary objective:",
      primaryGoal,
      "",
      "Content task:",
comparedEntity
  ? input.productArea === "local"
    ? `Build a page plan that helps ${accountName} compete with ${comparedEntity} for relevant local AI recommendations and buyer-intent searches.`
    : `Build a page plan that helps ${accountName} compete with ${comparedEntity} for relevant AI recommendations, comparison queries, alternative searches, and buyer-intent research.`
  : input.productArea === "local"
    ? `Build a page plan that helps ${accountName} improve local visibility for the opportunity above.`
    : `Build a page plan that helps ${accountName} improve competitive visibility for the opportunity above.`,
      "",
      "The output should be specific enough for a writer, marketer, or internal AI tool to create the page without needing to reinterpret the strategy.",
      "",
      "Include the following sections:",
      "",
      "1. Recommended H1",
      `- Write an H1 for a ${assetType}.`,
      `- Include ${accountName}.`,
      location ? `- Include ${location} only if geographically accurate.` : "- Include the location only if it is known and accurate.",
      "",
      "2. Page objective",
      "- Explain what this page should accomplish.",
      "- Tie the objective directly to the visibility issue and recommended action above.",
      "",
      "3. Intro angle",
      "- Write the recommended opening angle for the page.",
      "- Explain the customer problem or decision point this page should address.",
      comparedEntity
        ? `- Reference ${comparedEntity} only in a fair, factual, and non-defamatory way.`
        : "- Avoid unsupported competitor claims.",
      "",
      "4. Recommended page sections",
      "- Provide 5 to 7 section headings.",
      "- For each section, include 1 to 2 bullets explaining what the writer should cover.",
      input.productArea === "local"
        ? "- Focus on services, decision criteria, proof, trust, and local relevance."
        : "- Focus on category positioning, alternatives, comparisons, use cases, decision criteria, proof, and trust.",      "",
      "5. Proof points to gather before writing",
      ...differentiatorLines,
      "",
      input.productArea === "local"
         ? "6. Local/entity signals to include"
         : "6. Category/entity signals to include",
      input.productArea === "local"
        ? "- Service names and category terms customers actually use."
        : "- Product, platform, solution, category, and use-case terms buyers actually use.",
      input.productArea === "local"
         ? "- Accurate market, city, neighborhood, or service-area language."
         : "- Accurate category, industry, buyer-role, implementation, integration, and use-case language.",
      input.productArea === "local"
        ? "- Review themes, project examples, credentials, team/process details, and other trust signals if verified."
        : "- Verified proof points such as customer types, use cases, implementation strengths, platform capabilities, integrations, analyst recognition, or case studies.",
          "- Related entities, problems, materials, use cases, and decision criteria associated with the service/category.",
      "",
      "7. FAQ recommendations",
      "- Provide 5 to 7 FAQ questions the page should answer.",
      input.productArea === "local"
        ? "- Questions should reflect what a local buyer would ask before contacting the business."
        : "- Questions should reflect what a buyer would ask before comparing, shortlisting, or evaluating the solution.",
      "",
      "8. Internal linking ideas",
      "- Suggest related service pages, location pages, case studies, reviews/testimonials, gallery/project pages, and contact/quote pages that should link to or from this page.",
      "",
      "9. Call to action",
      "- Recommend a clear CTA that matches buyer intent.",
      "- Keep it practical and conversion-oriented.",
      "",
      "11. Structured data / schema recommendations", 
     "- Recommend relevant schema types for this content, such as LocalBusiness, Service, FAQPage, BreadcrumbList, or Review markup where appropriate.",
     "- Include FAQPage schema recommendations only for FAQs that will also appear visibly on the page.",
     "- Do not create fake reviews, ratings, prices, service areas, or claims in schema.",
     "- Identify which schema fields require business verification before publishing.",
     "",
     "12. Guardrails",
     "- Do not invent awards, reviews, certifications, service areas, pricing, guarantees, or competitor claims.",
      "- Use placeholders like [verify service area], [add review proof], or [insert project example] where details need confirmation.",
      "- Keep the tone helpful, expert, specific, and commercially practical.",
    ].join("\n"),

    content_brief: {
      objective: primaryGoal,
      audience,
      recommended_asset: assetType,
      opportunity_summary: knownSummary,
      known_facts: knownFacts,
      key_points: [
        knownRecommendation,
        comparedEntity
          ? `Clarify why ${accountName} should be considered alongside or instead of ${comparedEntity}.`
          : `Clarify why ${accountName} is relevant for this buyer-intent opportunity.`,
        "Make the page specific enough that AI systems can understand what the business does, who it serves, and why it is relevant.",
        "Add proof points and differentiators only where the business can verify them.",
        "Structure the content around buyer questions, decision criteria, trust-building information, and local/entity signals.",
      ],
      supporting_content_cluster: [
        comparedEntity
          ? `A comparison or alternative page that explains how buyers should evaluate ${accountName} and ${comparedEntity}.`
          : `A supporting page that expands on the main ${categoryLabel} decision criteria.`,
        "A FAQ or buyer-guide asset that answers common pre-contact questions.",
        "A proof-focused asset using verified reviews, credentials, project examples, case examples, or trust signals.",
        input.productArea === "local"
          ? "A local service-area or location-supporting page where geographically accurate."
          : "A related use-case, category, or integration page where relevant.",
      ],
      suggested_faqs: [
        `What should someone know before choosing a provider for ${categoryLabel}?`,
        `What makes ${accountName} relevant for this need?`,
        comparedEntity
          ? `How should buyers compare ${accountName} and ${comparedEntity}?`
          : "What should buyers compare before choosing a provider?",
        "What proof points should a customer review before making a decision?",
        "What service, location, or category details should be confirmed?",
      ],
    },

    suggested_structure: {
      h1:
        input.productArea === "local" && location
          ? `${categoryLabel} in ${location}`
          : `${categoryLabel}: What Buyers Should Know`,
      sections: [
        "Overview and customer intent",
        "Who this service or solution is for",
        "Key decision factors",
        comparedEntity
          ? `How to compare providers like ${comparedEntity}`
          : "How to compare providers",
        `Why ${accountName} is relevant`,
        "Proof points and trust signals to include",
        "Supporting content cluster and internal links",
        "Common questions",
        "Next step / call to action",
      ],
      seo_title:
        input.productArea === "local" && location
          ? `${categoryLabel} in ${location} | ${accountName}`
          : `${categoryLabel} Guide | ${accountName}`,
      meta_description:
        comparedEntity
          ? `Helpful guidance for comparing ${categoryLabel} providers and understanding why ${accountName} may be a strong fit.`
          : "Helpful, expert content designed to answer buyer questions, clarify relevance, and improve AI visibility.",
       schema_recommendations: [
  "LocalBusiness schema if accurate business name, location, service area, phone, URL, and sameAs profiles are available.",
  "Service schema for the core cabinet refinishing or related service described on the page.",
  "FAQPage schema for visible FAQ content only.",
  "BreadcrumbList schema if the page fits into the site hierarchy.",
  "Review or AggregateRating schema only if compliant, verified, and already visible on the page.",
],   
    },

    guardrails: [
      "Do not invent certifications, awards, reviews, service areas, pricing, or guarantees.",
      "Verify all factual claims before publishing.",
      "Avoid unsupported or negative competitor claims.",
      ...buildBrandGuidanceGuardrails(input),
    ],

    source_context: {
      productArea: input.productArea,
      opportunity: input.opportunity,
      accountContext: input.accountContext ?? {},
      brandGuidance: input.accountContext?.brandGuidance ?? null,
      comparedEntity,
      mock: true,
      template_version: "mock-v4-brand-guidance",
    },

    generation_status: "completed",
    generation_reason: "generated_from_governed_opportunity_brand_guidance_v14",

    model_provider: "toren",
    model_name: "deterministic_action_brief_brand_guidance_v14",
    prompt_template_version: "action_brief_brand_guidance_v14",

    input_tokens: null,
    output_tokens: null,
    estimated_cost: null,

    generated_by: userId,

    is_active: true,
    marked_complete_at: null,
    completed_by: null,
  };
}

export async function getOrCreateActionBrief(
  input: GenerateActionBriefInput
): Promise<GenerateActionBriefResult> {
  if (!input.accountId) {
    throw new Error("Missing accountId for action brief generation");
  }

  input = await applyAccountBrandGuidance(input);
  input = await applyActionBriefEntityGovernance(input);
  await deactivateStaleEntityGovernedBriefs(input);

  const opportunityKey = input.opportunityKey ?? buildOpportunityKey(input);

  const opportunityFingerprint =
    input.opportunityFingerprint ?? (await buildOpportunityFingerprint(input));

  const { data: existingBrief, error: existingError } = await supabase
    .from("opportunity_action_briefs")
    .select("*")
    .eq("account_id", input.accountId)
    .eq("product_area", input.productArea)
    .eq("opportunity_key", opportunityKey)
    .eq("opportunity_fingerprint", opportunityFingerprint)
    .eq("action_stage", 1)
    .maybeSingle();

  if (existingError) {
    throw existingError;
  }

  if (existingBrief) {
    await logActionBriefEvent({
      actionBriefId: existingBrief.id,
      accountId: input.accountId,
      eventType: "viewed",
      metadata: { source: "cache" },
    });

    return {
      actionBrief: existingBrief as ActionBrief,
      fromCache: true,
      completionMemoryMatch: false,
    };
  }

  if (input.productArea === "competitive") {
    const memoryStart = new Date(Date.now() - COMPETITIVE_COMPLETION_MEMORY_DAYS * 24 * 60 * 60 * 1000).toISOString();
    const { data: completedBriefs, error: completionMemoryError } = await supabase
      .from("opportunity_action_briefs")
      .select("*")
      .eq("account_id", input.accountId)
      .eq("product_area", "competitive")
      .eq("is_active", true)
      .not("marked_complete_at", "is", null)
      .gte("marked_complete_at", memoryStart)
      .order("marked_complete_at", { ascending: false });

    if (completionMemoryError) throw completionMemoryError;

    const incomingIdentity = getCompetitiveSemanticIdentity({
      productArea: input.productArea,
      opportunity: input.opportunity,
      accountContext: input.accountContext,
    });
    const matchedBrief = (completedBriefs || []).find((brief) => {
      const sourceContext = (brief.source_context || {}) as Record<string, unknown>;
      const identity = getCompetitiveSemanticIdentity({
        productArea: brief.product_area,
        actionType: brief.action_type,
        title: brief.title,
        sourceContext,
        opportunity: sourceContext.opportunity as GenerateActionBriefInput["opportunity"],
        accountContext: sourceContext.accountContext as GenerateActionBriefInput["accountContext"],
      });
      return Boolean(incomingIdentity.semanticKey && identity.semanticKey && incomingIdentity.semanticKey === identity.semanticKey);
    });

    if (matchedBrief) {
      await logActionBriefEvent({
        actionBriefId: matchedBrief.id,
        accountId: input.accountId,
        eventType: "semantic_completion_reused",
        metadata: {
          reason: "completed_competitive_semantic_identity_within_memory_window",
          attempted_opportunity_key: opportunityKey,
          attempted_opportunity_fingerprint: opportunityFingerprint,
          compared_entity: incomingIdentity.comparedEntity,
          action_family: incomingIdentity.actionFamily,
          run_id: input.runId ?? null,
          completion_memory_days: COMPETITIVE_COMPLETION_MEMORY_DAYS,
        },
      });

      return {
        actionBrief: matchedBrief as ActionBrief,
        fromCache: true,
        completionMemoryMatch: true,
      };
    }
  }

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;

  const payload = buildMockActionBriefPayload(
    input,
    opportunityKey,
    opportunityFingerprint,
    userId
  );

  const { data: createdBrief, error: insertError } = await supabase
    .from("opportunity_action_briefs")
    .insert(payload)
    .select("*")
    .single();

  if (insertError) {
    throw insertError;
  }

  await logActionBriefEvent({
    actionBriefId: createdBrief.id,
    accountId: input.accountId,
    eventType: "generated",
    metadata: {
      source: "mock_db_plumbing",
      product_area: input.productArea,
      opportunity_key: opportunityKey,
      prompt_template_version: "action_brief_governed_v13",
    },
  });

  return {
    actionBrief: createdBrief as ActionBrief,
    fromCache: false,
    completionMemoryMatch: false,
  };
}

export async function markActionBriefCreated(
  actionBriefId: string,
  accountId: string,
  currentlyComplete = false
): Promise<ActionBrief> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;
  const nextComplete = !currentlyComplete;

  const { data, error } = await supabase
    .from("opportunity_action_briefs")
    .update({
      marked_complete_at: nextComplete ? new Date().toISOString() : null,
      completed_by: nextComplete ? userId : null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", actionBriefId)
    .eq("account_id", accountId)
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  await logActionBriefEvent({
    actionBriefId,
    accountId,
    eventType: nextComplete ? "marked_complete" : "marked_incomplete",
    metadata: {
      source: "ui",
      previous_complete: currentlyComplete,
      next_complete: nextComplete,
    },
  });

  return data as ActionBrief;
}

export async function logActionBriefEvent({
  actionBriefId,
  accountId,
  eventType,
  metadata = {},
}: {
  actionBriefId: string;
  accountId: string;
  eventType: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;

  const { error } = await supabase
    .from("opportunity_action_brief_events")
    .insert({
      action_brief_id: actionBriefId,
      account_id: accountId,
      user_id: userId,
      event_type: eventType,
      event_metadata: metadata,
    });

  if (error) {
    console.warn("[ActionBrief] Event log failed", error);
  }
}
